import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { RULES, runRules } from "@/rules";
import type { RuleContext, RuleFiring } from "@/rules/types";

/**
 * Loads the snapshot the rules engine reads from + evaluates every rule.
 * One batched fan-out:
 *   - daily_vitals (last 30d)
 *   - sleep_record (last 30d)  — joined client-side by date for the
 *     `hrv_during_sleep_ms` + `bedtime_*_local` columns the recovery rules
 *     read from.
 *   - session (last 30d)
 *   - recovery_session (last 14d)
 *   - subjective_check_in (last 14d)
 *   - supplement (active rows only)
 *   - supplement_intake (last 14d)
 *   - pantry_item (all rows; client filters by expires_on)
 *
 * BW9: every computed firing is upserted into `rule_firing` so dismissals
 * and acknowledgements persist across sessions. The hook then re-reads the
 * `rule_firing` table to get the authoritative dismissal state and returns
 * those rows; dismiss / acknowledge mutations write back.
 */

export type PersistedFiring = RuleFiring & {
  firing_id: string;
  dismissed_at: string | null;
  acknowledged_at: string | null;
};

export type UseTodayRulesResult = {
  firings: PersistedFiring[];
  loading: boolean;
  error: unknown;
  dismiss: (firingId: string) => void;
  acknowledge: (firingId: string) => void;
};

const TZ_GUESS = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
})();

export function useTodayRules(now: Date = new Date()): UseTodayRulesResult {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();

  const q = useQuery<PersistedFiring[]>({
    queryKey: ["today_rules", userId],
    enabled: Boolean(userId),
    staleTime: 5 * 60_000,
    refetchInterval: 10 * 60_000,
    queryFn: async () => {
      if (!userId) return [];
      const ctx = await loadContext(userId, now);
      const computed = runRules(ctx, RULES);
      const today = isoDate(now);

      // Upsert every computed firing into rule_firing (idempotent per day)
      if (computed.length > 0) {
        const rows = computed.map((f) => ({
          user_id: userId,
          rule_id: f.rule_id,
          ts: now.toISOString(),
          tz: TZ_GUESS,
          fired_for_date: today,
          severity: f.severity,
          message: f.message,
          evidence_refs: f.evidence_refs,
          factors: f.factors as never,
        }));
        await supabase
          .from("rule_firing")
          .upsert(rows, {
            onConflict: "user_id,rule_id,fired_for_date",
            ignoreDuplicates: false,
          });
      }

      // Read authoritative state back from the table — keeps dismiss/ack
      // semantics centralized.
      const { data: persisted } = await supabase
        .from("rule_firing")
        .select(
          "id, rule_id, ts, severity, message, evidence_refs, factors, dismissed_at, acknowledged_at",
        )
        .eq("user_id", userId)
        .eq("fired_for_date", today)
        .order("ts", { ascending: false });

      return (persisted ?? []).map((row) => {
        const rawFactors = Array.isArray(row.factors)
          ? (row.factors as Array<Record<string, unknown>>)
          : [];
        const factors: RuleFiring["factors"] = rawFactors.map((f) => ({
          name: String(f.name ?? ""),
          value: (f.value as number | string) ?? "",
          contribution: String(f.contribution ?? ""),
          evidence_ref: String(f.evidence_ref ?? ""),
        }));
        return {
          firing_id: row.id,
          rule_id: row.rule_id,
          ts: row.ts,
          tz: TZ_GUESS,
          severity: row.severity as RuleFiring["severity"],
          message: row.message,
          evidence_refs: row.evidence_refs ?? [],
          factors,
          dismissed_at: row.dismissed_at,
          acknowledged_at: row.acknowledged_at,
        } satisfies PersistedFiring;
      });
    },
  });

  const dismissMut = useMutation({
    mutationFn: async (firingId: string) => {
      const { error } = await supabase
        .from("rule_firing")
        .update({ dismissed_at: new Date().toISOString(), dismissed: true })
        .eq("id", firingId);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["today_rules", userId] });
    },
  });

  const ackMut = useMutation({
    mutationFn: async (firingId: string) => {
      const { error } = await supabase
        .from("rule_firing")
        .update({
          acknowledged_at: new Date().toISOString(),
          acknowledged_by_user: true,
        })
        .eq("id", firingId);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["today_rules", userId] });
    },
  });

  return {
    firings: (q.data ?? []).filter((f) => f.dismissed_at == null),
    loading: q.isLoading,
    error: q.error,
    dismiss: (id) => dismissMut.mutate(id),
    acknowledge: (id) => ackMut.mutate(id),
  };
}

async function loadContext(userId: string, now: Date): Promise<RuleContext> {
  const since30 = isoDate(daysAgo(now, 30));
  const since30Iso = daysAgo(now, 30).toISOString();
  const since14Iso = daysAgo(now, 14).toISOString();

  const [
    vitalsRes,
    sleepRes,
    sessionRes,
    recoveryRes,
    subjRes,
    suppRes,
    intakeRes,
    pantryRes,
    profileRes,
    nutritionRollsRes,
    documentsRes,
  ] = await Promise.all([
    supabase
      .from("daily_vitals")
      .select(
        "date, hrv_status_ms, hrv_status_baseline_ms, rhr_bpm, rhr_baseline_bpm, steps, calories_active",
      )
      .eq("user_id", userId)
      .gte("date", since30),
    supabase
      .from("sleep_record")
      .select(
        "date, duration_min, hrv_during_sleep_ms, bedtime_actual_local, bedtime_target_local",
      )
      .eq("user_id", userId)
      .gte("date", since30),
    supabase
      .from("session")
      .select("id, start_ts, end_ts, sport_id, duration_min, tss, rpe")
      .eq("user_id", userId)
      .gte("end_ts", since30Iso),
    supabase
      .from("recovery_session")
      .select("id, ts, kind")
      .eq("user_id", userId)
      .gte("ts", since14Iso),
    supabase
      .from("subjective_check_in")
      .select("id, ts, prompt_kind, energy, mood, illness_symptoms")
      .eq("user_id", userId)
      .gte("ts", since14Iso),
    supabase
      .from("supplement")
      .select("id, name, status, schedule_frequency, times_of_day")
      .eq("user_id", userId)
      .eq("status", "active"),
    supabase
      .from("supplement_intake")
      .select("id, supplement_id, taken_at")
      .eq("user_id", userId)
      .gte("taken_at", since14Iso),
    supabase
      .from("pantry_item")
      .select("id, expires, food:food_id (name)")
      .eq("user_id", userId)
      .not("expires", "is", null),
    supabase
      .from("athlete_profile")
      .select("user_id, goal_weight_kg, current_weight_kg")
      .eq("user_id", userId)
      .maybeSingle(),
    supabase
      .from("nutrition_roll")
      .select("date, kcal_total, protein_g, carbs_g, fat_g, fiber_g, meals_logged")
      .eq("user_id", userId)
      .gte("date", since30),
    (supabase as unknown as { from: (t: string) => ReturnType<typeof supabase.from> })
      .from("document")
      .select("id, category, kind, title, expires_at")
      .eq("user_id", userId),
  ]);

  type SleepRow = NonNullable<typeof sleepRes.data>[number];
  type VitalsRow = NonNullable<typeof vitalsRes.data>[number];

  const sleepByDate = new Map<string, SleepRow>();
  for (const s of sleepRes.data ?? []) sleepByDate.set(s.date, s);

  const dates = new Set<string>();
  for (const v of vitalsRes.data ?? []) dates.add(v.date);
  for (const s of sleepRes.data ?? []) dates.add(s.date);

  const vitalsByDate = new Map<string, VitalsRow>();
  for (const v of vitalsRes.data ?? []) vitalsByDate.set(v.date, v);

  const vitals = [...dates].sort().map((date) => {
    const v = vitalsByDate.get(date);
    const s = sleepByDate.get(date);
    return {
      date,
      hrv_status_ms: v?.hrv_status_ms != null ? Number(v.hrv_status_ms) : null,
      rhr_bpm: v?.rhr_bpm ?? null,
      hrv_baseline_ms:
        v?.hrv_status_baseline_ms != null
          ? Number(v.hrv_status_baseline_ms)
          : null,
      rhr_baseline_bpm: v?.rhr_baseline_bpm ?? null,
      steps: v?.steps ?? null,
      calories_active: v?.calories_active ?? null,
      sleep_min: s?.duration_min ?? null,
      hrv_during_sleep_ms:
        s?.hrv_during_sleep_ms != null ? Number(s.hrv_during_sleep_ms) : null,
      bedtime_actual_local: s?.bedtime_actual_local ?? null,
      bedtime_target_local: s?.bedtime_target_local ?? null,
    };
  });

  return {
    user_id: userId,
    now,
    tz: TZ_GUESS,
    vitals,
    sessions: (sessionRes.data ?? []).map((s) => ({
      ...s,
      tss: s.tss != null ? Number(s.tss) : null,
      duration_min: Number(s.duration_min),
    })),
    recovery_sessions: recoveryRes.data ?? [],
    subjective: (subjRes.data ?? []).map((s) => ({
      id: s.id,
      ts: s.ts,
      prompt_kind: s.prompt_kind,
      energy: s.energy,
      mood: s.mood,
      illness_symptoms: s.illness_symptoms ?? [],
    })),
    supplements: (suppRes.data ?? []).map((s) => ({
      id: s.id,
      name: s.name,
      status: s.status,
      schedule_frequency: s.schedule_frequency,
      times_of_day: s.times_of_day ?? [],
    })),
    supplement_intakes: intakeRes.data ?? [],
    pantry: (pantryRes.data ?? []).map((p) => {
      const row = p as unknown as {
        id: string;
        expires: string | null;
        food: { name: string } | { name: string }[] | null;
      };
      const foodRel = Array.isArray(row.food) ? row.food[0] : row.food;
      return {
        id: row.id,
        name: foodRel?.name ?? "(unknown item)",
        expires_on: row.expires ?? null,
      };
    }),
    profile: profileRes.data
      ? {
          user_id: profileRes.data.user_id,
          goal_weight_kg: profileRes.data.goal_weight_kg,
          current_weight_kg: profileRes.data.current_weight_kg,
        }
      : null,
    nutrition_rolls: (nutritionRollsRes.data ?? []).map((r) => ({
      date: r.date,
      kcal_total: Number(r.kcal_total ?? 0),
      protein_g: Number(r.protein_g ?? 0),
      carbs_g: Number(r.carbs_g ?? 0),
      fat_g: Number(r.fat_g ?? 0),
      fiber_g: Number(r.fiber_g ?? 0),
      meals_logged: Number(r.meals_logged ?? 0),
    })),
    documents: ((documentsRes.data ?? []) as unknown as Array<{
      id: string;
      category: string;
      kind: string;
      title: string;
      expires_at: string | null;
    }>).map((d) => ({
      id: d.id,
      category: d.category,
      kind: d.kind,
      title: d.title,
      expires_at: d.expires_at,
    })),
  };
}

function daysAgo(d: Date, n: number): Date {
  return new Date(d.getTime() - n * 86_400_000);
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
